import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../components/envago/Input/Input';
import OptionalLabel from '../../../../../components/envago/OptionalLabel/OptionalLabel';
import Information, { InformationType } from '../../../../../components/envago/Information/Information';
import {
    CapturedValuesInputState,
    CapturedValuesVisibility,
    formatPhoneAsYouType,
    normalizePhone,
    validateEmail,
} from '../JobDetails.capturedValues';
import {
    formatOtherRegionOption,
    formatPhoneRegionOption,
    OTHER_REGION,
    PhoneRegionSelection,
    removeCallingCode,
    resolveEffectivePhoneRegion,
    resolvePhoneRegionOptions,
} from '../JobDetails.phoneRegion';

interface CapturedValuesSectionProps {
    visibility: CapturedValuesVisibility;
    phoneRequired: boolean;
    emailRequired: boolean;
    defaultPhoneRegion: string;
    value: CapturedValuesInputState;
    onChange: (next: CapturedValuesInputState) => void;
    /** Die Ablesewerte sind vollständig — ab jetzt darf ein fehlendes Pflichtfeld gemeldet werden. */
    submitAttempted?: boolean;
    /** Tippause, nach der eine unlesbare Eingabe gemeldet wird. Aus der Ablese-Konfiguration. */
    validationFeedbackDelay?: number;
}

/**
 * Gibt ein Fragment aus Label/Wert-Paaren zurück, KEIN umschließendes Element.
 *
 * Das ist keine Geschmacksfrage: OptionalLabel liefert zwei Geschwisterknoten (Beschriftung und
 * Wert), die der Elterncontainer als zwei Zellen seines Rasters aufnimmt. Ein Wrapper-div
 * ringsum wäre EIN Rasterkind und presst Beschriftung, Feld und Hinweis in die schmale
 * Beschriftungsspalte.
 */
const CapturedValuesSection = ({
    visibility,
    phoneRequired,
    emailRequired,
    defaultPhoneRegion,
    value,
    onChange,
    submitAttempted = false,
    validationFeedbackDelay = 1500,
}: CapturedValuesSectionProps) => {
    const { t } = useTranslation();

    /*
     * Eine unlesbare Eingabe wird nach einer Tippause gemeldet, nicht bei jedem Zeichen und
     * nicht erst beim Verlassen des Feldes. Beide Extreme waren falsch:
     *
     * Beim Verlassen: auf dem iPhone schließt die Tastatur über ihre eigene Leiste, ohne ein
     * onBlur auszulösen. Die Meldung blieb aus, während der Absenden-Knopf gesperrt war.
     *
     * Bei jedem Zeichen: dann steht die Meldung schon nach der ersten Ziffer da und begleitet
     * das Tippen, obwohl die Nummer nur noch unvollständig ist.
     *
     * Die Tippause ist das Muster, das der Ablesewert daneben schon nutzt
     * (meterReading.validationFeedbackDelay, Vorgabe 1500 ms) — dort aus demselben Grund.
     */
    const [showDelayedWarning, setShowDelayedWarning] = useState(false);
    const warningTimer = useRef<ReturnType<typeof setTimeout>>();

    const restartWarningTimer = () => {
        if (warningTimer.current) {
            clearTimeout(warningTimer.current);
        }
        setShowDelayedWarning(false);
        warningTimer.current = setTimeout(() => setShowDelayedWarning(true), validationFeedbackDelay);
    };

    useEffect(
        () => () => {
            if (warningTimer.current) {
                clearTimeout(warningTimer.current);
            }
        },
        [],
    );

    if (!visibility.showBlock) return null;

    // Die wirksame Region wird abgeleitet, nicht gehalten: der Zustand kennt allein die
    // ausdrückliche Wahl des Ablesers. Damit können Auswahl und Eingabe nicht auseinanderlaufen.
    const { selection } = resolveEffectivePhoneRegion({
        chosen: value.phoneRegion,
        defaultRegion: defaultPhoneRegion,
        phone: value.phone,
    });
    const regionOptions = resolvePhoneRegionOptions(defaultPhoneRegion);

    const phoneResult = normalizePhone(value.phone, selection);
    const emailResult = validateEmail(value.email);

    // Beim Absendeversuch sofort: dann ist der Knopf gesperrt und der Grund muss sichtbar sein,
    // ohne dass der Ableser auf eine Tippause wartet.
    const mayWarn = showDelayedWarning || submitAttempted;

    // Bei "Anderes Land" soll dastehen, WAS fehlt, nicht nur dass etwas nicht stimmt.
    const phoneInvalidMessage =
        phoneResult.reason === 'missingCountryCode'
            ? t('jobs.details.capturedValues.warnings.phoneCountryCodeMissing')
            : t('jobs.details.capturedValues.warnings.phoneInvalid');

    const phoneWarning = !phoneResult.valid
        ? mayWarn
            ? phoneInvalidMessage
            : undefined
        : phoneRequired && !phoneResult.value && submitAttempted
        ? t('jobs.details.capturedValues.warnings.phoneRequired')
        : undefined;

    const emailWarning = !emailResult.valid
        ? mayWarn
            ? t('jobs.details.capturedValues.warnings.emailInvalid')
            : undefined
        : emailRequired && !emailResult.value && submitAttempted
        ? t('jobs.details.capturedValues.warnings.emailRequired')
        : undefined;

    const label = (text: string, required: boolean) => (required ? `${text} *` : text);

    // Die Erläuterung steht nur am obersten sichtbaren Feld, damit sie sich nicht wiederholt.
    const anyRequired = (visibility.showPhone && phoneRequired) || (visibility.showEmail && emailRequired);
    const description = anyRequired
        ? t('jobs.details.capturedValues.descriptionRequired')
        : t('jobs.details.capturedValues.description');

    const handleRegionChange = (nextSelection: PhoneRegionSelection) => {
        restartWarningTimer();
        onChange({
            ...value,
            phoneRegion: nextSelection,
            // Trägt die Eingabe eine Vorwahl, wird sie entfernt — sie steht dann in der Auswahl.
            // Ohne das bliebe die Auswahl auf "Andere", und der Klick wirkte wirkungslos.
            phone: removeCallingCode({ phone: value.phone, nextSelection }),
        });
    };

    return (
        <>
            {visibility.showPhone && (
                <OptionalLabel
                    label={label(t('jobs.details.capturedValues.labels.phone'), phoneRequired)}
                    description={description}
                >
                    <>
                        {/*
                         * Ein natives <select>, kein eigenes Aufklappmenü: dieselben Maße und
                         * Ränder wie Input (vgl. ShareJobDialog), damit sich beide in der Zeile
                         * von selbst ausrichten — und auf dem Telefon öffnet das Betriebssystem
                         * seinen eigenen Auswahlring.
                         */}
                        <div className={'flex flex-row items-start gap-2'}>
                            <select
                                name={'capturedPhoneRegion'}
                                aria-label={t('jobs.details.capturedValues.labels.phoneRegion')}
                                className={'form-select rounded-md border w-32 flex-none h-8 leading-8 py-0 text-black mt-2 mb-2'}
                                value={selection}
                                onChange={(event) => handleRegionChange(event.target.value as PhoneRegionSelection)}
                            >
                                {regionOptions.map((country) => (
                                    <option key={country} value={country}>
                                        {formatPhoneRegionOption(country)}
                                    </option>
                                ))}
                                <option value={OTHER_REGION}>
                                    {formatOtherRegionOption(t('jobs.details.capturedValues.labels.otherRegion'))}
                                </option>
                            </select>
                            <div className={'flex-1'}>
                                <Input
                                    name={'capturedPhone'}
                                    type={'tel'}
                                    inputMode={'tel'}
                                    autoComplete={'off'}
                                    placeholder={
                                        selection === OTHER_REGION
                                            ? t('jobs.details.capturedValues.placeholders.phoneInternational')
                                            : t('jobs.details.capturedValues.placeholders.phone')
                                    }
                                    value={value.phone}
                                    maxLength={40}
                                    onChange={(phone) => {
                                        restartWarningTimer();
                                        onChange({
                                            ...value,
                                            phone: formatPhoneAsYouType(phone, value.phone, selection),
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        {phoneWarning && <Information type={InformationType.WARNING}>{phoneWarning}</Information>}
                    </>
                </OptionalLabel>
            )}

            {visibility.showEmail && (
                <OptionalLabel
                    label={label(t('jobs.details.capturedValues.labels.email'), emailRequired)}
                    description={visibility.showPhone ? undefined : description}
                >
                    <>
                        <Input
                            name={'capturedEmail'}
                            type={'email'}
                            inputMode={'email'}
                            autoComplete={'off'}
                            placeholder={t('jobs.details.capturedValues.placeholders.email')}
                            value={value.email}
                            maxLength={254}
                            onChange={(email) => {
                                restartWarningTimer();
                                onChange({ ...value, email });
                            }}
                        />
                        {emailWarning && <Information type={InformationType.WARNING}>{emailWarning}</Information>}
                    </>
                </OptionalLabel>
            )}
        </>
    );
};

export default CapturedValuesSection;
